/**
 * practiceDrillHelper
 *
 * Shared experiment building blocks for surfaces that are not /experiments: the
 * status vocabulary every surface reads a row's rail from, the nav card session
 * detail lists an experiment with, and the drill that opens that experiment's
 * real detail panel in the same right panel (back arrow returning).
 *
 * NOT a full sibling of `sessionDrillHelper`/`patternDrillHelper`, which build
 * their payloads without a live section: the experiment detail is ~750 lines of
 * instance-bound rendering on PracticesSection, so the DRILL half reaches that
 * section instance (`window.app.modules.practicesSection`) rather than copying
 * it. The RENDER half here is pure.
 *
 * @module components/practiceDrillHelper
 */

import page from '../vendor/page.js';
import { eventBus } from '../core/utils.js';
import { renderNavCard } from './navCard.js';

/** Milliseconds in a day — the window-close arithmetic's unit. */
const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Whole days until an experiment's observation window closes: 0 today, negative
 * once it has passed, null when the row carries no window (an unreconciled
 * legacy row). Day-granular, so a window closing later today still reads as
 * "Due today" rather than overdue.
 *
 * @param {Object} practice - Practice row
 * @returns {number|null} Days until close, or null
 */
export function daysUntilWindowClose(practice) {
  if (!practice?.windowClosesAt) return null;
  const closes = new Date(practice.windowClosesAt);
  if (Number.isNaN(closes.getTime())) return null;
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  return Math.round((startOfDay(closes) - startOfDay(new Date())) / DAY_MS);
}

/**
 * The right-rail status for one experiment. An ACTIVE row has exactly two states
 * — inside its observation window, or "Overdue" once that window has passed —
 * and nothing else: no separate kept-open state (extending the window just puts
 * the row back on track). On-track is the default for every active row, so it
 * earns no colored dot; the dot is reserved for the states that actually need
 * attention.
 *
 * Full matrix:
 *   completed               → the close date (plain gray) — a finding closed its
 *                             own loop, so it reports when, not what is due. It
 *                             MUST come before the window check: a finding's
 *                             window has always passed, and without this arm
 *                             every finding would read "Overdue" in orange.
 *   archived                → "Archived" (gray) — the only way rows get there
 *   active, window ahead    → "Due today" / "Due tomorrow" / "Due in N days"
 *                             (plain gray) — relative, like "Overdue" is
 *   active, no window       → "No due date" (plain gray) — unreconciled legacy row
 *   active, window past     → "Overdue" (orange)
 *
 * /experiments renders findings through its own richer finding anatomy and never
 * reaches the `completed` arm; session detail lists every non-draft row of a
 * session, where findings are the common case on an older session.
 *
 * @param {Object} practice - Practice row
 * @returns {{text: string, cls: string, indicatorColor: string|null}}
 */
export function practiceStatusInfo(practice) {
  if (practice?.status === 'completed') {
    const closedAt = practice.completedAt ? new Date(practice.completedAt) : null;
    const text = closedAt && !Number.isNaN(closedAt.getTime())
      ? `Closed ${closedAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
      : 'Closed';
    return { text, cls: 'date', indicatorColor: null };
  }

  // Every archived row got there by a deliberate user Archive now. Historical
  // rows carrying `closeReason: 'lapsed'` (from the retired auto-lapse sweep)
  // and pre-cutover rows carrying none both read the same way: the user's
  // shelf. `cls` stays the internal `set-aside` token the CSS is keyed on.
  if (practice?.status === 'archived') {
    return { text: 'Archived', cls: 'set-aside', indicatorColor: 'gray' };
  }

  const days = daysUntilWindowClose(practice);
  if (days !== null && days < 0) {
    return { text: 'Overdue', cls: 'overdue', indicatorColor: 'orange' };
  }

  // Null window = a legacy row the reconciliation sweep hasn't reached yet —
  // there's no date to show, so it reads "No due date" rather than a fabricated
  // one.
  let dueText = 'No due date';
  if (days === 0) dueText = 'Due today';
  else if (days === 1) dueText = 'Due tomorrow';
  else if (days !== null) dueText = `Due in ${days} days`;
  return { text: dueText, cls: 'due', indicatorColor: null };
}

/**
 * The name of an experiment wherever it is listed away from /experiments: the
 * EXPERIMENT's own title in every state, never a closed row's finding name. The
 * finding name is what /experiments calls a closed row, because that page is
 * where you go looking for a learning by its name; a session note is a record of
 * what that session produced, and what it produced was the experiment.
 *
 * @param {Object} practice - Practice row
 * @returns {string} Plain (unescaped) title
 */
export function practiceCardTitle(practice) {
  const title = typeof practice?.title === 'string' ? practice.title.trim() : '';
  return title || practice?.definition?.name || 'Experiment';
}

/**
 * One experiment nav card for a page that links out to it. Pure — the caller
 * wires its own click delegation on `data-practice-id`.
 *
 * Both ids ride the card: the UUID identifies the row for the in-panel drill,
 * and `practiceNumber` is what the /experiments deep link takes when the panel
 * is too deep to drill (`?practice=` parses a NUMBER, never a UUID).
 *
 * @param {Object} practice - Serialized practice row (`serializePractice` shape)
 * @returns {string} HTML string
 */
export function renderPracticeNavCard(practice) {
  const { text, cls, indicatorColor } = practiceStatusInfo(practice);
  const railInner = indicatorColor
    ? `<span class="notification-label notification-label--${indicatorColor}">${text}</span>`
    : `<span class="note-row__date">${text}</span>`;

  return renderNavCard({
    title: practiceCardTitle(practice),
    dataAttrs: {
      'data-practice-id': practice.id,
      'data-practice-number': practice.practiceNumber ?? null
    },
    // No per-kind card class: the experiment card needs nothing the shared
    // shape doesn't give it, and `.practice-card` is /experiments' own list-row
    // geometry (min-height, a 132px rail) — borrowing it here would couple this
    // card to that page's list.
    cardClass: `nav-card--status-${cls}`,
    // The action says what the experiment actually asks of you — the one line
    // worth carrying beside the title, same role the thread card's context has.
    snippet: practice.action || '',
    railHtml: `<span class="note-row__status">${railInner}</span>`
  });
}

/**
 * Open an experiment's detail from a card inside the current right panel.
 *
 * Takes the row itself (session detail already holds the fully serialized
 * practice), so there is no second fetch to fail and no dependence on the
 * /experiments list having been loaded. The section instance is still needed for
 * the detail render, and is seeded with this row so its in-body handlers resolve.
 *
 * Falls back to the /experiments deep link when the panel is at its drill cap or
 * the section isn't mounted.
 *
 * @param {Object} practice - Serialized practice row
 * @returns {void}
 */
export function openPracticeInPanel(practice) {
  if (!practice?.id) return;

  const rightPanelManager = window.app?.modules?.rightPanelManager;
  const section = window.app?.modules?.practicesSection;

  if (section && rightPanelManager?.canDrill?.()) {
    try {
      eventBus.emit('right-panel:drill', section.buildDrilledPracticePayload(practice));
      return;
    } catch (err) {
      console.error('[practiceDrillHelper] Failed to build practice detail for drill:', err);
    }
  }

  // Fallback: the /experiments deep link. `?practice=` resolves a practiceNumber
  // — a UUID here would parse to NaN and silently open nothing.
  if (practice.practiceNumber) {
    page(`/practices?practice=${practice.practiceNumber}`);
  } else {
    page('/practices');
  }
}
